// INVDESIGN1-S2 (docs/build-briefs/INVDESIGN1-S2-home-setup-review-order.md; owner-approved mockups invdesign1-1, -2 and -6). Draws
// the Inventory home into #function-hub-inventory: the next count action, the shelves in walking order, what to order, what is on
// the way and the stock value (or, while the location is still entering starting stock, the setup state; or, right after a count
// closes, "Count closed" and what to order). What it says is ui/src/inventoryHomeModel.ts; every figure is an existing read.
//
// A read is made only when the session holds its permission (hasPermission, never a role name); a card whose read failed says so
// and shows no figure. Nothing here writes: Count, Resume and Recount open the count (admin.startCountFromHome), Build order opens
// the cart. Nothing is remembered between visits.

import {
  getWhatToOrder,
  getInventoryHomeAreas,
  getStockValueReport,
  listPurchaseOrders,
  listStockFlags,
  type InventoryHomeAreasDto,
  type PurchaseOrderSummaryDto,
  type StockValueReportDto,
  type WhatToOrderDto,
} from "./api.ts";
import type { CountClosedSummary } from "./countTableModel.ts";
import {
  areaRowsForHome,
  buildButtonLabel,
  heroView,
  homeMode,
  lowIds,
  onTheWay,
  ordersByVendor,
  setupView,
  vendorTotalText,
  type AreaRowView,
  type Chip,
  type OrderTarget,
  type VendorOrderView,
} from "./inventoryHomeModel.ts";
import { formatCartMoney } from "./purchasingDisplay.ts";
import { formatMoney } from "./reportFormat.ts";
import { hasPermission, type Session } from "./session.ts";
import { signedMoney, signedPercent } from "./countTableModel.ts";

export type InventoryHomeTarget =
  | { readonly kind: "view"; readonly view: "stockLevels" | "counts"; readonly past?: boolean }
  | OrderTarget
  | { readonly kind: "orders" }
  | { readonly kind: "settings" };

export interface InventoryHomeDeps {
  openTarget(target: InventoryHomeTarget): void;
  /** Count, Resume or Recount: opens the one open count (starting one if none is open) in the area layout. */
  startCount(options: { areaId?: string | null }): void;
  now(): number;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className !== undefined) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function button(
  className: string,
  label: string,
  testId: string,
  onTap: () => void,
  ariaLabel?: string,
): HTMLButtonElement {
  const node = el("button", className, label);
  node.type = "button";
  node.dataset["testid"] = testId;
  if (ariaLabel !== undefined) node.setAttribute("aria-label", ariaLabel);
  node.addEventListener("click", onTap);
  return node;
}

function chip(value: Chip): HTMLElement {
  const node = el("span", `ih-chip ih-chip-${value.tone}`, value.text);
  node.dataset["testid"] = "inv-area-chip";
  return node;
}

function meter(done: number, total: number, testId: string): HTMLElement {
  const track = el("div", "ih-meter");
  track.setAttribute("role", "progressbar");
  track.setAttribute("aria-valuemin", "0");
  track.setAttribute("aria-valuemax", String(total));
  track.setAttribute("aria-valuenow", String(done));
  track.dataset["testid"] = testId;
  const fill = el("i");
  fill.style.width = total === 0 ? "0%" : `${Math.min(100, Math.round((done / total) * 100))}%`;
  track.append(fill);
  return track;
}

function card(className = ""): HTMLElement {
  return el("div", `ih-card ${className}`.trim());
}

// ---------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------

interface Reads {
  readonly areas: InventoryHomeAreasDto | undefined;
  /** True when the person may read areas at all; false hides the card, true with `areas` undefined means the read failed. */
  readonly mayAreas: boolean;
  readonly stock: StockValueReportDto | undefined;
  readonly mayStock: boolean;
  readonly whatToOrder: WhatToOrderDto | undefined;
  readonly mayOrder: boolean;
  readonly orders: PurchaseOrderSummaryDto[] | undefined;
  readonly mayOrders: boolean;
  readonly flagged86: number | undefined;
}

function ok<T extends object>(result: { status: number; body: T }): T | undefined {
  return result.status === 200 ? result.body : undefined;
}

async function readAll(session: Session): Promise<Reads> {
  const may = (id: string): boolean => hasPermission(session, id);
  const base = session.apiBaseUrl;
  const tenant = session.tenantId;
  const mayAreas = may("inv.count_session.run");
  const mayStock = may("report.stock_value.view");
  const mayOrder = may("purchasing.cart.manage");
  const mayOrders = may("purchasing.order.view");
  const may86 = may("inv.stock_flag.view");
  const [areas, stock, proposals, orders, flags] = await Promise.all([
    mayAreas ? getInventoryHomeAreas(base, tenant).then(ok) : undefined,
    mayStock ? getStockValueReport(base, tenant).then(ok) : undefined,
    mayOrder ? getWhatToOrder(base, tenant).then(ok) : undefined,
    mayOrders ? listPurchaseOrders(base, tenant).then(ok) : undefined,
    may86 ? listStockFlags(base, tenant, { latestOnly: true }).then(ok) : undefined,
  ]);
  const on = flags === undefined ? undefined : new Set(flags.stockFlags.filter((f) => f.flagged).map((f) => f.liquorProductId));
  return {
    areas,
    mayAreas,
    stock,
    mayStock,
    whatToOrder: proposals?.whatToOrder,
    mayOrder,
    orders: orders?.orders,
    mayOrders,
    flagged86: on?.size,
  };
}

// ---------------------------------------------------------------------
// Pieces
// ---------------------------------------------------------------------

function areaTable(rows: readonly AreaRowView[], deps: InventoryHomeDeps, setup: boolean): HTMLElement {
  const table = el("div", "ih-areas");
  table.dataset["testid"] = "inv-areas";
  for (const row of rows) {
    const node = el("div", `ih-area ih-area-${row.mark}${row.primary ? " is-next" : ""}`);
    node.dataset["testid"] = "inv-area-row";
    node.dataset["areaName"] = row.name;
    node.dataset["mark"] = row.mark;
    const mark = el("span", `ih-num ih-num-${row.mark}`, row.mark === "done" ? "✓" : String(row.position));
    mark.setAttribute("aria-hidden", "true");
    const name = el("div", "ih-area-name");
    name.append(el("strong", undefined, row.name), el("span", "ih-area-detail", row.detail));
    name.querySelector("span")?.setAttribute("data-testid", "inv-area-detail");
    node.append(mark, name);
    if (setup && row.progress !== undefined) {
      const progress = el("div", "ih-area-progress");
      progress.append(
        meter(row.progress.done, row.progress.total, "inv-area-meter"),
        el(
          "span",
          "ih-area-detail",
          row.progress.done === 0 ? "Not counted yet" : `${row.progress.done} of ${row.progress.total} counted`,
        ),
      );
      node.append(progress);
    }
    const chips = el("div", "ih-area-chips");
    for (const value of row.chips) chips.append(chip(value));
    node.append(chips);
    const action = button(
      `btn-${row.primary ? "primary" : "secondary"} ih-btn`,
      row.actionLabel,
      "inv-area-action",
      () => deps.startCount({ areaId: row.areaId }),
      `${row.actionLabel}: ${row.name}`,
    );
    node.append(action);
    table.append(node);
  }
  return table;
}

function vendorCard(vendor: VendorOrderView, deps: InventoryHomeDeps, full: boolean): HTMLElement {
  const node = card("ih-vendor");
  node.dataset["testid"] = "inv-order-vendor";
  node.dataset["vendor"] = vendor.vendorName;
  const head = el("div", "ih-vendor-head");
  const title = el("div", "ih-vendor-title");
  title.append(
    el("strong", "ih-vendor-name", vendor.vendorName),
    el("span", "ih-area-detail", `${vendor.lines.length} ${vendor.lines.length === 1 ? "item" : "items"}`),
  );
  const total = el("strong", "ih-vendor-total", vendorTotalText(vendor));
  total.dataset["testid"] = "inv-order-total";
  head.append(title, total);
  node.append(head);
  if (full) {
    const list = el("div", "ih-lines");
    for (const line of vendor.lines) {
      const row = el("div", "ih-line");
      row.dataset["testid"] = "inv-order-line";
      const name = el("div", "ih-line-name");
      name.append(el("strong", undefined, line.name));
      if (line.note !== null) name.append(el("span", "ih-area-detail", line.note));
      const has = el("span", "ih-line-has", line.hasText);
      const amount = el("strong", "ih-line-amount", line.orderText);
      amount.dataset["testid"] = "inv-order-amount";
      row.append(name, has, amount);
      list.append(row);
    }
    node.append(list);
  }
  const actions = el("div", "ih-vendor-actions");
  actions.append(
    button(
      "btn-primary ih-btn ih-btn-wide",
      buildButtonLabel(vendor.vendorName),
      "inv-build-order",
      () => deps.openTarget({ kind: "build-order", vendorId: vendor.vendorId }),
    ),
  );
  if (full) {
    actions.append(
      button("btn-secondary ih-btn", "Edit amounts", "inv-edit-amounts", () =>
        deps.openTarget({ kind: "edit-amounts", vendorId: vendor.vendorId }),
      ),
    );
  }
  node.append(actions);
  return node;
}

function toOrderCard(reads: Reads, deps: InventoryHomeDeps, setup: boolean): HTMLElement | undefined {
  if (!reads.mayOrder) return undefined;
  const node = card();
  node.dataset["testid"] = "inv-to-order";
  const head = el("div", "ih-card-head");
  head.append(el("h2", "ih-h2", "To order"));
  node.append(head);
  if (setup) {
    node.append(el("p", "ih-big-note", "Not counted yet"));
    node.append(
      el(
        "p",
        "ih-muted",
        "Par fills itself in from sales once you have a few weeks of counts. Until then, set par by hand if you like.",
      ),
    );
    return node;
  }
  if (reads.whatToOrder === undefined) {
    node.append(el("p", "ih-muted", "Could not load what to order. Try again in a moment."));
    return node;
  }
  const { vendors, needsSetup } = ordersByVendor(reads.whatToOrder);
  const itemCount = vendors.reduce((sum, vendor) => sum + vendor.lines.length, 0);
  head.append(el("span", `ih-chip ih-chip-${itemCount > 0 ? "low" : "neutral"}`, `${itemCount} ${itemCount === 1 ? "item" : "items"} low`));
  if (itemCount === 0) {
    node.append(el("p", "ih-muted", "Nothing is below par. Nothing to order."));
    return node;
  }
  node.append(el("p", "ih-muted ih-sm", "Amounts are filled in from par. They update as you finish the count."));
  for (const vendor of vendors) node.append(vendorCard(vendor, deps, false));
  if (needsSetup > 0) {
    node.append(
      el(
        "p",
        "ih-muted ih-sm",
        `${needsSetup} low ${needsSetup === 1 ? "item needs" : "items need"} a vendor or cost set up before it can be suggested.`,
      ),
    );
  }
  return node;
}

function onTheWayCard(reads: Reads): HTMLElement | undefined {
  if (!reads.mayOrders) return undefined;
  const node = card();
  node.dataset["testid"] = "inv-on-the-way";
  node.append(el("p", "ih-eyebrow", "On the way"));
  if (reads.orders === undefined) {
    node.append(el("p", "ih-muted", "Could not load orders."));
    return node;
  }
  const orders = onTheWay(reads.orders);
  if (orders.length === 0) {
    node.append(el("p", "ih-muted", "Nothing is on its way."));
    return node;
  }
  for (const order of orders.slice(0, 3)) {
    const row = el("div", "ih-way");
    row.dataset["testid"] = "inv-way-row";
    const text = el("div");
    text.append(
      el("strong", undefined, order.vendorName),
      el("div", "ih-area-detail", `${order.lineCount} ${order.lineCount === 1 ? "line" : "lines"}`),
    );
    row.append(text, el("span", "ih-chip ih-chip-info", formatCartMoney(order.estimatedTotalCents)));
    node.append(row);
  }
  if (orders.length > 3) node.append(el("p", "ih-muted ih-sm", `${orders.length - 3} more on the way`));
  return node;
}

function stockValueCard(reads: Reads, setup: boolean): HTMLElement | undefined {
  if (!reads.mayStock) return undefined;
  const node = card("ih-value");
  node.dataset["testid"] = "inv-stock-value";
  const left = el("div");
  left.append(el("p", "ih-eyebrow", "Stock value"));
  const right = el("div", "ih-area-detail ih-right");
  if (setup) {
    const note = el("div", "ih-big-note", "Not counted yet");
    note.dataset["testid"] = "inv-stock-value-figure";
    left.append(note, el("p", "ih-muted", "Shows after the first count is closed."));
    node.append(left);
    return node;
  }
  if (reads.stock === undefined) {
    left.append(el("div", "ih-muted", "Could not load the stock value."));
    node.append(left);
    return node;
  }
  const figure = el("div", "ih-value-figure", formatMoney(reads.stock.totals.valueCents));
  figure.dataset["testid"] = "inv-stock-value-figure";
  left.append(figure);
  const lines: string[] = [];
  if (reads.flagged86 !== undefined) {
    lines.push(reads.flagged86 === 0 ? "86 list: nothing off" : `86 list: ${reads.flagged86} off`);
  }
  right.textContent = lines.join(" · ");
  node.append(left, right);
  return node;
}

// ---------------------------------------------------------------------
// The three screens
// ---------------------------------------------------------------------

function drawHome(
  host: HTMLElement,
  reads: Reads,
  areas: InventoryHomeAreasDto,
  deps: InventoryHomeDeps,
): void {
  const now = deps.now();
  const hero = heroView(areas, now);
  const heroCard = card("ih-hero");
  heroCard.dataset["testid"] = "inv-hero";
  const text = el("div", "ih-hero-text");
  text.append(el("p", "ih-eyebrow", hero.eyebrow));
  const title = el("h2", "ih-h1", hero.title);
  title.dataset["testid"] = "inv-hero-title";
  const body = el("p", "ih-muted", hero.body);
  body.dataset["testid"] = "inv-hero-body";
  text.append(title, body);
  heroCard.append(text);
  if (hero.progress !== undefined) {
    const progress = el("div", "ih-hero-progress");
    const line = el("p", "ih-sm ih-muted", hero.progress.text);
    line.dataset["testid"] = "inv-hero-progress";
    progress.append(line, meter(hero.progress.done, hero.progress.total, "inv-hero-meter"));
    heroCard.append(progress);
  }
  heroCard.append(
    button("btn-primary ih-btn ih-btn-big", hero.actionLabel, "inv-hero-action", () =>
      deps.startCount(hero.areaId === undefined ? {} : { areaId: hero.areaId }),
    ),
  );

  const grid = el("div", "ih-grid");
  const shelves = card("ih-shelves");
  const shelvesHead = el("div", "ih-card-head ih-pad");
  const heading = el("div");
  heading.append(
    el("h2", "ih-h2", "Walk the shelves"),
    el("p", "ih-muted ih-sm", "Areas in the order you walk them. Low means below par."),
  );
  shelvesHead.append(heading);
  shelves.append(shelvesHead);
  const low = lowIds(reads.stock);
  if (areas.areas.length === 0) {
    shelves.append(el("p", "ih-muted ih-pad", "No items to count yet. Add items, then name the areas you walk."));
  } else {
    shelves.append(areaTable(areaRowsForHome(areas, low, now), deps, false));
  }
  const side = el("div", "ih-side");
  for (const part of [toOrderCard(reads, deps, false), onTheWayCard(reads), stockValueCard(reads, false)]) {
    if (part !== undefined) side.append(part);
  }
  grid.append(shelves, side);
  host.append(heroCard, grid);
}

function drawSetup(
  host: HTMLElement,
  reads: Reads,
  areas: InventoryHomeAreasDto,
  deps: InventoryHomeDeps,
): void {
  const setup = setupView(areas);
  const head = el("div", "ih-setup-head");
  head.append(
    el("p", "ih-eyebrow", "New location"),
    el("h2", "ih-h1", "Set up your starting stock"),
    el(
      "p",
      "ih-muted ih-lead",
      "Count what is on each shelf once. After that, low items, par and orders work on their own. Stock value and low stock show up after the first count.",
    ),
  );
  const progress = card("ih-setup-progress");
  progress.dataset["testid"] = "inv-setup-progress";
  const left = el("div", "ih-setup-left");
  const done = el("strong", "ih-h3", `${setup.areasDone} of ${setup.areasTotal} areas done`);
  done.dataset["testid"] = "inv-setup-done";
  left.append(done, meter(setup.areasDone, setup.areasTotal, "inv-setup-meter"));
  const steps = el("ol", "ih-steps");
  steps.dataset["testid"] = "inv-setup-steps";
  const firstOpenStep = setup.steps.findIndex((step) => !step.done);
  setup.steps.forEach((step, index) => {
    const item = el("li", `ih-step${step.done ? " is-done" : ""}${index === firstOpenStep ? " is-now" : ""}`);
    item.textContent = `Step ${index + 1} ${step.label}${step.done ? " ✓" : ""}`;
    steps.append(item);
  });
  progress.append(
    left,
    steps,
    button("btn-primary ih-btn ih-btn-big", setup.actionLabel, "inv-hero-action", () =>
      deps.startCount(setup.actionAreaId === undefined ? {} : { areaId: setup.actionAreaId }),
    ),
  );
  const grid = el("div", "ih-grid ih-grid-setup");
  const shelves = card("ih-shelves");
  shelves.append(areaTable(setup.rows, deps, true));
  const side = el("div", "ih-side");
  const value = stockValueCard(reads, true);
  if (value !== undefined) side.append(value);
  const ordering = toOrderCard(reads, deps, true);
  if (ordering !== undefined) {
    ordering.append(
      button("btn-secondary ih-btn", "Set par by hand", "inv-setup-set-par", () =>
        deps.openTarget({ kind: "settings" }),
      ),
    );
    side.append(ordering);
  }
  if (reads.mayOrders) {
    const receive = card();
    receive.dataset["testid"] = "inv-setup-receive";
    receive.append(
      el("h2", "ih-h2", "Have a bottle count already?"),
      el("p", "ih-muted ih-sm", "Add stock from a delivery or a transfer if you do not want to count."),
      button("btn-secondary ih-btn", "Receive an order", "inv-setup-receive-button", () =>
        deps.openTarget({ kind: "orders" }),
      ),
    );
    side.append(receive);
  }
  grid.append(shelves, side);
  host.append(head, progress, grid);
}

function drawAfterCount(
  host: HTMLElement,
  reads: Reads,
  areas: InventoryHomeAreasDto | undefined,
  summary: CountClosedSummary,
  deps: InventoryHomeDeps,
  showHome: () => void,
): void {
  const closed = areas?.lastClosedCount ?? null;
  const when =
    closed === null
      ? "just now"
      : `${new Intl.DateTimeFormat("en-US", { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }).format(new Date(closed.closedAt))}${closed.closedByName === null ? "" : ` by ${closed.closedByName}`}`;
  const off =
    summary.differenceCents === null
      ? ""
      : summary.differenceCents === 0
        ? " Nothing was off."
        : ` Off by ${signedMoney(summary.differenceCents)}${summary.percent === null ? "" : ` (${signedPercent(summary.percent)})`}.`;
  const banner = card("ih-banner");
  banner.dataset["testid"] = "inv-count-closed";
  const mark = el("span", "ih-num ih-num-done ih-num-big", "✓");
  mark.setAttribute("aria-hidden", "true");
  const words = el("div", "ih-banner-text");
  const closedTitle = el("h2", "ih-h1", "Count closed");
  closedTitle.dataset["testid"] = "inv-count-closed-title";
  const detail = el(
    "p",
    "ih-muted",
    `${when}. ${summary.itemsCounted} ${summary.itemsCounted === 1 ? "item" : "items"} counted.${off}`,
  );
  detail.dataset["testid"] = "inv-count-closed-detail";
  words.append(closedTitle, detail);
  banner.append(
    mark,
    words,
    button("btn-secondary ih-btn", "See the count", "inv-see-count", () =>
      deps.openTarget({ kind: "view", view: "counts", past: true }),
    ),
  );

  const heading = el("div", "ih-after-head");
  heading.append(el("h2", "ih-h1", "What to order"));
  const orders = el("div", "ih-vendors");
  orders.dataset["testid"] = "inv-after-orders";
  if (!reads.mayOrder) {
    heading.append(el("span", "ih-muted", "Ordering is not part of your access."));
  } else if (reads.whatToOrder === undefined) {
    heading.append(el("span", "ih-muted", "Could not load what to order. Try again in a moment."));
  } else if (reads.whatToOrder.vendors.length === 0) {
    heading.append(el("span", "ih-muted", "Nothing is below par. Nothing to order."));
  } else {
    const { vendors, needsSetup } = ordersByVendor(reads.whatToOrder);
    const count = vendors.reduce((sum, vendor) => sum + vendor.lines.length, 0);
    heading.append(
      el(
        "span",
        "ih-muted",
        `${count} ${count === 1 ? "item needs" : "items need"} ordering. Amounts come from par, minus what is on hand and on order.`,
      ),
    );
    for (const vendor of vendors) orders.append(vendorCard(vendor, deps, true));
    if (needsSetup > 0) {
      orders.append(
        el(
          "p",
          "ih-muted ih-sm",
          `${needsSetup} low ${needsSetup === 1 ? "item needs" : "items need"} a vendor or cost set up before it can be suggested.`,
        ),
      );
    }
  }
  const foot = el("p", "ih-muted ih-after-foot");
  if (reads.stock !== undefined) {
    const lowCount = lowIds(reads.stock)?.size ?? 0;
    foot.append(`Not low: ${Math.max(0, reads.stock.items.length - lowCount)} items. `);
  }
  foot.append(
    button("ih-link", "See all stock levels", "inv-see-stock-levels", () =>
      deps.openTarget({ kind: "view", view: "stockLevels" }),
    ),
    " ",
    button("ih-link", "Back to Inventory", "inv-after-home", showHome),
  );
  host.append(banner, heading, orders, foot);
}

// ---------------------------------------------------------------------
// Entry point
// ---------------------------------------------------------------------

/** Reads what the person may read and draws the home, the setup, or (with `afterCount`) the after-count screen into `host`.
 * `isCurrent` is false once a newer open, sign-out or navigation has superseded this one, and the late response is dropped. */
export async function renderInventoryHome(
  session: Session,
  host: HTMLElement,
  deps: InventoryHomeDeps,
  afterCount: CountClosedSummary | undefined,
  isCurrent: () => boolean,
): Promise<void> {
  host.dataset["state"] = "loading";
  host.replaceChildren();
  const setParButton = document.getElementById("inventory-set-par-button");
  if (setParButton instanceof HTMLElement) {
    setParButton.hidden = !hasPermission(session, "admin.liquor_product.view");
  }
  let reads: Reads;
  try {
    reads = await readAll(session);
  } catch {
    if (!isCurrent()) return;
    host.dataset["state"] = "error";
    host.replaceChildren(el("p", "ih-muted", "Could not load Inventory. Check your connection and open it again."));
    return;
  }
  if (!isCurrent()) return;
  const draw = (): void => {
    host.replaceChildren();
    if (!reads.mayAreas && afterCount === undefined) {
      // A person who may not run counts still sees what to order, what is on the way and the stock value.
      host.dataset["state"] = "home";
      const side = el("div", "ih-side ih-side-wide");
      for (const part of [toOrderCard(reads, deps, false), onTheWayCard(reads), stockValueCard(reads, false)]) {
        if (part !== undefined) side.append(part);
      }
      host.append(side);
      return;
    }
    if (reads.areas === undefined && afterCount === undefined) {
      host.dataset["state"] = "error";
      host.append(el("p", "ih-muted", "Could not load the shelves. Check your connection and open Inventory again."));
      return;
    }
    if (afterCount !== undefined && !showHomeInstead) {
      host.dataset["state"] = "after-count";
      drawAfterCount(host, reads, reads.areas, afterCount, deps, () => {
        showHomeInstead = true;
        draw();
      });
      return;
    }
    const areas = reads.areas;
    if (areas === undefined) return;
    const mode = homeMode(areas);
    host.dataset["state"] = mode;
    if (mode === "setup") drawSetup(host, reads, areas, deps);
    else drawHome(host, reads, areas, deps);
  };
  let showHomeInstead = false;
  draw();
}

export function resetInventoryHome(): void {
  const host = document.querySelector<HTMLElement>("[data-inv-home]");
  if (host === null) return;
  host.replaceChildren();
  delete host.dataset["state"];
}
